import React, { useState } from 'react';

/**
 * ScreenerFlow
 *
 * Props:
 *   questions  — array of screener_question rows (ordered by order_index)
 *   onComplete — called with { answers: { [questionId]: value } } when done
 */
const ScreenerFlow = ({ questions, onComplete }) => {
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const question = questions[index];
  const total = questions.length;
  const selected = answers[question.id];
  const canAdvance = !question.is_required || selected !== undefined;

  const handleSelect = (value) => {
    setAnswers(prev => ({ ...prev, [question.id]: value }));
  };

  const handleNext = async () => {
    if (!canAdvance) return;

    if (index < total - 1) {
      setIndex(i => i + 1);
    } else {
      setSubmitting(true);
      await onComplete({ answers });
      // parent controls what happens next
    }
  };

  const isLast = index === total - 1;
  const progress = ((index + 1) / total) * 100;

  return (
    <div className="h-screen w-full flex flex-col items-center justify-center bg-rutabaga-bg px-6">
      <div className="w-full max-w-lg">

        {/* Progress */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            Question {index + 1} of {total}
          </span>
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            {Math.round(progress)}%
          </span>
        </div>
        <div className="w-full h-1 bg-gray-200 rounded-full mb-8">
          <div
            className="h-1 bg-rutabaga-pink rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8">

          {/* Question */}
          <h2 className="text-lg font-black text-rutabaga-dark tracking-tight mb-6 leading-snug">
            {question.question_text}
            {question.is_required && (
              <span className="text-rutabaga-pink ml-1">*</span>
            )}
          </h2>

          {/* Options */}
          {question.question_type === 'single_select' && (
            <div className="space-y-2">
              {(question.options || []).map((opt) => {
                const isSelected = selected === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt.value)}
                    className={`w-full text-left px-4 py-3.5 rounded-md border-2 font-bold text-sm transition-all
                      ${isSelected
                        ? 'border-rutabaga-pink bg-rutabaga-pink/5 text-rutabaga-dark'
                        : 'border-gray-100 bg-white text-gray-600 hover:border-gray-200 hover:bg-gray-50'
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 shrink-0 flex items-center justify-center
                        ${isSelected ? 'border-rutabaga-pink' : 'border-gray-300'}`}
                      >
                        {isSelected && (
                          <div className="w-2 h-2 rounded-full bg-rutabaga-pink" />
                        )}
                      </div>
                      {opt.label}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Text input for open questions */}
          {question.question_type === 'text' && (
            <textarea
              className="rutabaga-input min-h-[100px] resize-none text-sm"
              placeholder="Type your answer here…"
              value={selected || ''}
              onChange={(e) => handleSelect(e.target.value)}
            />
          )}

          {/* Next / Submit */}
          <button
            onClick={handleNext}
            disabled={!canAdvance || submitting}
            className="rutabaga-button-primary w-full mt-6 disabled:opacity-40"
          >
            {submitting ? 'Submitting…' : isLast ? 'Submit' : 'Next →'}
          </button>
        </div>

        {/* Branding */}
        <p className="text-center text-[10px] text-gray-300 font-bold uppercase tracking-widest mt-6">
          Powered by Rutabaga
        </p>
      </div>
    </div>
  );
};

export default ScreenerFlow;
